import { useEffect, useState, useCallback, useRef } from "react";
import { ShieldAlert, Loader2, LogOut, Phone, MapPin, CheckCircle2, RefreshCw, User, Link2, Eye, EyeOff, Flag } from "lucide-react";
import { toast } from "sonner";
import { RecaptchaVerifier, signInWithPhoneNumber } from "firebase/auth";
import { useTranslation } from "react-i18next";
import { getToken, setToken, clearToken, photoUrl, friendlyError } from "@/lib/api";
import { ageRangeLabelT, reporterLabelT, statusLabelT } from "@/lib/labels";
import { adminApi } from "@/lib/endpoints";
import { auth, firebaseEnabled } from "@/lib/firebase";
import { PhoneInput } from "@/components/PhoneInput";

function Login({ onLogin }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState(firebaseEnabled ? "firebase" : "dev");
  const [step, setStep] = useState(1);
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [devSecret, setDevSecret] = useState("");
  const [loading, setLoading] = useState(false);
  const [devCode, setDevCode] = useState("");
  const recaptchaRef = useRef(null);
  const confirmationRef = useRef(null);
  const isDev = mode === "dev";

  const resetRecaptcha = () => {
    if (recaptchaRef.current) { try { recaptchaRef.current.clear(); } catch (e) {} recaptchaRef.current = null; }
  };

  const sendFirebaseCode = async () => {
    if (!phone) { toast.error(t("admin.toastEnterPhone58")); return; }
    setLoading(true);
    try {
      if (!recaptchaRef.current) {
        recaptchaRef.current = new RecaptchaVerifier(auth, "recaptcha-container", { size: "invisible" });
      }
      confirmationRef.current = await signInWithPhoneNumber(auth, phone, recaptchaRef.current);
      setStep(2);
      toast.success(t("admin.toastCodeSent"));
    } catch (e) {
      resetRecaptcha();
      const msg = String(e?.code || e?.message || "").toLowerCase();
      if (msg.includes("invalid-phone-number")) {
        toast.error(t("admin.toastInvalidPhone58"));
      } else if (msg.includes("too-many-requests")) {
        toast.error(t("admin.toastTooMany"));
      } else if (msg.includes("billing-not-enabled") || msg.includes("billing")) {
        toast.error(t("admin.toastBilling"), { duration: 7000 });
        setMode("dev"); setStep(1);
      } else if (msg.includes("captcha") || msg.includes("argument-error")) {
        toast.error(t("admin.toastCaptcha"), { duration: 7000 });
      } else if (msg.includes("operation-not-allowed")) {
        toast.error(t("admin.toastOpNotAllowed"), { duration: 7000 });
        setMode("dev"); setStep(1);
      } else {
        toast.error(t("admin.toastSmsFailed"), { duration: 7000 });
      }
    } finally { setLoading(false); }
  };

  const verifyFirebase = async () => {
    if (!confirmationRef.current) { toast.error(t("admin.toastRequestFirst")); return; }
    setLoading(true);
    try {
      const cred = await confirmationRef.current.confirm(code);
      const idToken = await cred.user.getIdToken();
      const data = await adminApi.firebaseLogin(idToken);
      setToken(data.token);
      onLogin();
    } catch (e) {
      toast.error(friendlyError(e, t("admin.toastWrongCode")));
    } finally { setLoading(false); }
  };

  const requestOtp = async () => {
    if (!phone) { toast.error(t("admin.toastEnterPhone")); return; }
    setLoading(true);
    try {
      const data = await adminApi.requestOtp(phone, devSecret);
      setStep(2);
      if (data.dev_code) { setDevCode(data.dev_code); toast.message(t("admin.toastDevMode")); }
      else toast.success(t("admin.toastCodeSent"));
    } catch (e) {
      toast.error(friendlyError(e));
    } finally { setLoading(false); }
  };

  const verifyOtp = async () => {
    setLoading(true);
    try {
      const data = await adminApi.verifyOtp(phone, code);
      setToken(data.token);
      onLogin();
    } catch (e) {
      toast.error(friendlyError(e, t("admin.toastWrongCode")));
    } finally { setLoading(false); }
  };

  const onSend = isDev ? requestOtp : sendFirebaseCode;
  const onVerify = isDev ? verifyOtp : verifyFirebase;
  const switchMode = () => {
    resetRecaptcha(); confirmationRef.current = null;
    setStep(1); setCode(""); setDevCode(""); setMode(isDev ? "firebase" : "dev");
  };

  const inputCls = "w-full min-h-[48px] rounded-sm border border-neutral-300 bg-white px-3 text-base focus:border-[#0066CC] focus:ring-1 focus:ring-[#0066CC] outline-none";

  return (
    <div className="max-w-sm mx-auto flex flex-col gap-4 py-6" data-testid="admin-login">
      <div className="flex flex-col items-center gap-2 text-center">
        <ShieldAlert className="w-10 h-10 text-[#0066CC]" />
        <h1 className="font-heading text-2xl font-bold">{t("admin.loginTitle")}</h1>
        <p className="text-sm text-neutral-600">
          {isDev ? t("admin.loginSubDev") : t("admin.loginSubFirebase")}
        </p>
      </div>
      {step === 1 ? (
        <>
          <PhoneInput testid="admin-phone" value={phone} onChange={setPhone} placeholder={t("admin.phonePlaceholder")} />
          {isDev && (
            <input data-testid="admin-dev-secret" type="password" className={inputCls} value={devSecret}
              onChange={(e) => setDevSecret(e.target.value)} placeholder={t("admin.devSecretPlaceholder")} />
          )}
          <button data-testid="request-otp-btn" onClick={onSend} disabled={loading}
            className="bg-[#0066CC] text-white hover:bg-[#0052A3] disabled:opacity-60 rounded-sm min-h-[48px] font-semibold flex items-center justify-center gap-2">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t("admin.sendCode")}
          </button>
        </>
      ) : (
        <>
          {devCode && (
            <div className="text-center text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-sm p-2" data-testid="dev-code">
              {t("admin.devCode")} <span className="font-bold tracking-widest">{devCode}</span>
            </div>
          )}
          <input data-testid="admin-otp-input" className={inputCls} value={code}
            onChange={(e) => setCode(e.target.value)} placeholder={t("admin.otpPlaceholder")} inputMode="numeric" />
          <button data-testid="verify-otp-btn" onClick={onVerify} disabled={loading}
            className="bg-[#0066CC] text-white hover:bg-[#0052A3] disabled:opacity-60 rounded-sm min-h-[48px] font-semibold flex items-center justify-center gap-2">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t("admin.enter")}
          </button>
          <button onClick={() => { resetRecaptcha(); setStep(1); }} className="text-sm text-neutral-500 hover:text-[#0066CC]">{t("admin.changeNumber")}</button>
        </>
      )}
      <div id="recaptcha-container" />
      {firebaseEnabled && (
        <button onClick={switchMode} data-testid="toggle-dev-mode"
          className="text-xs text-neutral-400 hover:text-[#0066CC] mt-2">
          {isDev ? t("admin.backToFirebase") : t("admin.devAccess")}
        </button>
      )}
    </div>
  );
}


function Stat({ label, value, color }) {
  return (
    <div className="bg-white border border-neutral-200 rounded-sm p-3 text-center">
      <div className={`text-2xl font-heading font-bold ${color}`}>{value}</div>
      <div className="text-xs uppercase tracking-wider text-neutral-500 mt-1">{label}</div>
    </div>
  );
}

function Panel({ onLogout }) {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [section, setSection] = useState("claims"); // claims | matches | seekers | abuse
  const [claims, setClaims] = useState([]);
  const [matches, setMatches] = useState([]);
  const [seekers, setSeekers] = useState([]);
  const [abuse, setAbuse] = useState([]);
  const [tab, setTab] = useState("pendiente");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const tasks = [adminApi.stats()];
      if (section === "claims") tasks.push(adminApi.claims(tab === "todas" ? undefined : tab));
      else if (section === "matches") tasks.push(adminApi.matches());
      else if (section === "abuse") tasks.push(adminApi.abuseReports());
      else tasks.push(adminApi.seekers());
      const [statsData, sectionData] = await Promise.all(tasks);
      setStats(statsData);
      if (section === "claims") setClaims(sectionData);
      else if (section === "matches") setMatches(sectionData);
      else if (section === "abuse") setAbuse(sectionData);
      else setSeekers(sectionData);
    } catch (e) {
      if (e.response?.status === 401) { toast.error(t("admin.toastSessionExpired")); onLogout(); }
      else toast.error(friendlyError(e));
    } finally { setLoading(false); }
  }, [section, tab, onLogout, t]);

  useEffect(() => { load(); }, [load]);

  const setClaimStatus = async (claimId, status) => {
    try {
      await adminApi.updateClaim(claimId, status);
      toast.success(t("admin.toastUpdated"));
      load();
    } catch (e) { toast.error(friendlyError(e, t("admin.toastUpdateError"))); }
  };

  const toggleCaseVisibility = async (caseId, hidden) => {
    try {
      await adminApi.setCaseVisibility(caseId, hidden);
      toast.success(hidden ? t("admin.toastCaseHidden") : t("admin.toastCaseVisible"));
      load();
    } catch (e) { toast.error(friendlyError(e)); }
  };

  const toggleSeekerVisibility = async (seekerId, hidden) => {
    try {
      await adminApi.setSeekerVisibility(seekerId, hidden);
      toast.success(hidden ? t("admin.toastSeekerHidden") : t("admin.toastSeekerVisible"));
      load();
    } catch (e) { toast.error(friendlyError(e)); }
  };

  const togglePostVisibility = async (postId, hidden) => {
    try {
      await adminApi.setPostVisibility(postId, hidden);
      toast.success(hidden ? t("admin.toastPostHidden") : t("admin.toastPostVisible"));
      load();
    } catch (e) { toast.error(friendlyError(e)); }
  };

  const resolveAbuse = async (id) => {
    try {
      await adminApi.resolveAbuse(id, "revisada");
      toast.success(t("admin.toastAlertReviewed"));
      load();
    } catch (e) { toast.error(friendlyError(e)); }
  };

  const markLocated = async (caseId) => {
    try {
      await adminApi.markCaseStatus(caseId, "localizada");
      toast.success(t("admin.toastMarkedLocated"));
      load();
    } catch (e) { toast.error(friendlyError(e)); }
  };

  const markSeekerLocated = async (seekerId) => {
    try {
      await adminApi.markSeeker(seekerId, "localizada");
      toast.success(t("admin.toastSeekerReunited"));
      load();
    } catch (e) { toast.error(friendlyError(e)); }
  };

  return (
    <div className="flex flex-col gap-4" data-testid="admin-panel">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold">{t("admin.title")}</h1>
        <button onClick={onLogout} data-testid="logout-btn"
          className="text-sm text-neutral-500 hover:text-[#B91C1C] flex items-center gap-1">
          <LogOut className="w-4 h-4" /> {t("admin.logout")}
        </button>
      </div>

      {stats && (
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          <Stat label={t("admin.statFound")} value={stats.total} color="text-neutral-900" />
          <Stat label={t("admin.statSearching")} value={stats.buscando} color="text-[#B45309]" />
          <Stat label={t("admin.statLocated")} value={stats.localizada} color="text-[#166534]" />
          <Stat label={t("admin.statClaims")} value={stats.pending_claims} color="text-[#0066CC]" />
          <Stat label={t("admin.statSeekers")} value={stats.seekers} color="text-neutral-900" />
          <Stat label={t("admin.statAlerts")} value={stats.pending_abuse ?? 0} color="text-[#B91C1C]" />
        </div>
      )}

      <div className="flex items-center justify-between gap-2">
        <div className="flex gap-2 flex-wrap">
          {[["claims", t("admin.secClaims")], ["matches", t("admin.secMatches")], ["seekers", t("admin.secSeekers")], ["abuse", t("admin.secAbuse")]].map(([v, l]) => (
            <button key={v} onClick={() => setSection(v)} data-testid={`section-${v}`}
              className={`text-sm font-semibold px-3 py-2 rounded-sm border ${section === v ? "bg-[#0066CC] text-white border-[#0066CC]" : "bg-white border-neutral-300 text-neutral-700"}`}>
              {l}
            </button>
          ))}
        </div>
        <button onClick={load} data-testid="refresh-btn" className="text-neutral-500 hover:text-[#0066CC] shrink-0">
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {section === "claims" && (
        <div className="flex gap-2">
          {[["pendiente", t("admin.tabPending")], ["confirmada", t("admin.tabConfirmed")], ["todas", t("admin.tabAll")]].map(([v, l]) => (
            <button key={v} onClick={() => setTab(v)} data-testid={`tab-${v}`}
              className={`text-sm font-semibold px-3 py-2 rounded-sm border ${tab === v ? "bg-neutral-900 text-white border-neutral-900" : "bg-white border-neutral-300 text-neutral-700"}`}>
              {l}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <p className="text-center text-neutral-500 py-8">{t("common.loading")}</p>
      ) : section === "claims" ? (
        claims.length === 0 ? (
          <p className="text-center text-neutral-500 py-10 border border-dashed border-neutral-300 rounded-sm" data-testid="no-claims">
            {t("admin.noClaims")}
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            {claims.map((cl) => (
              <div key={cl.id} className="bg-white border border-neutral-200 rounded-sm p-3 flex flex-col gap-2" data-testid={`claim-${cl.id}`}>
                <div className="flex gap-3">
                  {cl.case && (
                    <img src={photoUrl(cl.case.photo_path)} alt="" className="w-16 h-20 object-cover rounded-sm bg-neutral-200 shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    {cl.case && (
                      <>
                        <p className="font-semibold">
                          {cl.case.name_unknown ? t("card.nameUnknown") : `${cl.case.first_name} ${cl.case.last_name || ""}`} · {ageRangeLabelT(cl.case.age_range, t)}
                        </p>
                        <p className="text-xs text-neutral-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> {cl.case.municipality}</p>
                        <p className="text-xs text-neutral-600 mt-1">
                          {t("admin.shelter")} <span className="font-medium">{cl.case.shelter_name}</span> · {reporterLabelT(cl.case.reporter_type, t)}
                        </p>
                        <p className="text-xs text-neutral-600">{t("admin.foundIn")} {cl.case.found_location}</p>
                        {cl.case.name_unknown && (
                          <p className="text-xs text-neutral-600">
                            {[cl.case.eye_color && `${t("admin.eyes")} ${cl.case.eye_color}`, cl.case.skin_color && `${t("admin.skin")} ${cl.case.skin_color}`, cl.case.distinguishing_marks && `${t("admin.marks")} ${cl.case.distinguishing_marks}`, cl.case.clothing && `${t("admin.clothing")} ${cl.case.clothing}`].filter(Boolean).join(" · ")}
                          </p>
                        )}
                        <p className="text-xs text-neutral-600 flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {t("admin.reporter")} {cl.case.reporter_phone}
                        </p>
                        {cl.case.lat && cl.case.lng && (
                          <a href={`https://www.google.com/maps?q=${cl.case.lat},${cl.case.lng}`} target="_blank" rel="noopener noreferrer"
                            className="text-[#0066CC] text-xs flex items-center gap-1 hover:underline" data-testid={`case-pin-${cl.case.id}`}>
                            <Link2 className="w-3 h-3" /> {t("admin.viewExactPin")}
                          </a>
                        )}
                      </>
                    )}
                  </div>
                  <span className={`text-xs font-semibold h-fit px-2 py-1 rounded-sm ${
                    cl.status === "pendiente" ? "bg-blue-50 text-[#0066CC]" :
                    cl.status === "confirmada" ? "bg-green-50 text-[#166534]" : "bg-red-50 text-[#B91C1C]"}`}>
                    {statusLabelT(cl.status, t)}
                  </span>
                </div>

                <div className="border-t border-neutral-100 pt-2 text-sm">
                  {cl.review_priority && cl.flags?.length > 0 && (
                    <div className="mb-2 bg-red-50 border border-red-200 rounded-sm p-2" data-testid={`claim-flags-${cl.id}`}>
                      <p className="text-xs font-bold text-[#B91C1C] flex items-center gap-1">{t("admin.reviewPriority")}</p>
                      {cl.flags.map((f) => <p key={f} className="text-xs text-[#B91C1C]">· {f}</p>)}
                    </div>
                  )}
                  <p className="font-semibold text-neutral-800">{t("admin.claimant")} {cl.claimant_name}</p>
                  <p className="text-neutral-600">{t("admin.cedulaRel", { cedula: cl.cedula, rel: cl.relationship })}</p>
                  <p className="text-neutral-600 flex items-center gap-1"><Phone className="w-3 h-3" /> {cl.claimant_phone}</p>
                  {(cl.clothing_description || cl.marks_description || cl.lost_contact_date || cl.lost_contact_place) && (
                    <div className="text-neutral-600 text-xs mt-1 bg-neutral-50 border border-neutral-200 rounded-sm p-2">
                      <p className="font-semibold text-neutral-700">{t("admin.evidenceProvided")}</p>
                      {cl.clothing_description && <p>{t("admin.clothing")} {cl.clothing_description}</p>}
                      {cl.marks_description && <p>{t("admin.marks")} {cl.marks_description}</p>}
                      {cl.lost_contact_date && <p>{t("admin.lostContact")} {cl.lost_contact_date}</p>}
                      {cl.lost_contact_place && <p>{t("admin.place")} {cl.lost_contact_place}</p>}
                    </div>
                  )}
                </div>

                {cl.status === "pendiente" && (
                  <div className="flex gap-2">
                    <button onClick={() => setClaimStatus(cl.id, "confirmada")} data-testid={`confirm-claim-${cl.id}`}
                      className="flex-1 bg-[#166534] text-white hover:opacity-90 rounded-sm min-h-[40px] text-sm font-semibold">
                      {t("admin.confirmMatch")}
                    </button>
                    <button onClick={() => setClaimStatus(cl.id, "rechazada")} data-testid={`reject-claim-${cl.id}`}
                      className="flex-1 bg-white border border-neutral-300 hover:bg-neutral-50 rounded-sm min-h-[40px] text-sm font-semibold">
                      {t("admin.reject")}
                    </button>
                  </div>
                )}
                {cl.case && cl.case.status !== "localizada" && (
                  <button onClick={() => markLocated(cl.case.id)} data-testid={`mark-located-${cl.case.id}`}
                    className="w-full bg-[#0066CC] text-white hover:bg-[#0052A3] rounded-sm min-h-[40px] text-sm font-semibold flex items-center justify-center gap-1">
                    <CheckCircle2 className="w-4 h-4" /> {t("admin.markLocated")}
                  </button>
                )}
                {cl.case && (
                  <button onClick={() => toggleCaseVisibility(cl.case.id, !cl.case.hidden)} data-testid={`toggle-case-visibility-${cl.case.id}`}
                    className={`w-full rounded-sm min-h-[40px] text-sm font-semibold flex items-center justify-center gap-1 border ${cl.case.hidden ? "bg-white border-[#166534] text-[#166534]" : "bg-white border-[#B91C1C] text-[#B91C1C] hover:bg-red-50"}`}>
                    {cl.case.hidden ? <><Eye className="w-4 h-4" /> {t("admin.showCase")}</> : <><EyeOff className="w-4 h-4" /> {t("admin.hideCase")}</>}
                  </button>
                )}
              </div>
            ))}
          </div>
        )
      ) : section === "matches" ? (
        matches.length === 0 ? (
          <p className="text-center text-neutral-500 py-10 border border-dashed border-neutral-300 rounded-sm" data-testid="no-matches">
            {t("admin.noMatches")}
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            <p className="text-xs text-neutral-500">{t("admin.matchHint")}</p>
            {matches.map((m, i) => (
              <div key={m.case?.id || `match-${i}`} className="bg-white border border-neutral-200 rounded-sm p-3 flex flex-col gap-2" data-testid={`match-${i}`}>
                <div className="flex flex-wrap gap-1">
                  {m.reasons.map((r) => (
                    <span key={r} className="text-[11px] font-semibold bg-amber-50 text-[#B45309] border border-amber-200 px-2 py-0.5 rounded-sm">{r}</span>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="border border-[#0066CC]/30 rounded-sm p-2">
                    <p className="text-[10px] font-bold uppercase text-[#0066CC] mb-1">{t("admin.childFound")}</p>
                    <div className="flex gap-2">
                      <img src={photoUrl(m.case.photo_path)} alt="" className="w-12 h-16 object-cover rounded-sm bg-neutral-200 shrink-0" />
                      <div className="min-w-0 text-xs text-neutral-600">
                        <p className="font-semibold text-neutral-800">{m.case.name_unknown ? t("admin.noName") : m.case.first_name}</p>
                        <p>{ageRangeLabelT(m.case.age_range, t)}</p>
                        <p className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {m.case.municipality}, {m.case.state}</p>
                        <p>{t("admin.shelter")} {m.case.shelter_name}</p>
                        <p className="flex items-center gap-1"><Phone className="w-3 h-3" /> {m.case.reporter_phone}</p>
                        {m.case.lat && m.case.lng && (
                          <a href={`https://www.google.com/maps?q=${m.case.lat},${m.case.lng}`} target="_blank" rel="noopener noreferrer"
                            className="text-[#0066CC] flex items-center gap-1 hover:underline" data-testid={`match-case-pin-${m.case.id}`}>
                            <Link2 className="w-3 h-3" /> {t("admin.exactPin")}
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="border border-[#B45309]/30 rounded-sm p-2">
                    <p className="text-[10px] font-bold uppercase text-[#B45309] mb-1">{t("admin.familySearch")}</p>
                    <div className="flex gap-2">
                      <img src={photoUrl(m.seeker.photos[0].path)} alt="" className="w-12 h-16 object-cover rounded-sm bg-neutral-200 shrink-0" />
                      <div className="min-w-0 text-xs text-neutral-600">
                        <p className="font-semibold text-neutral-800">{m.seeker.child_name}</p>
                        <p>{ageRangeLabelT(m.seeker.age_range, t)}</p>
                        <p className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {m.seeker.municipality}, {m.seeker.state}</p>
                        <p className="flex items-center gap-1"><User className="w-3 h-3" /> {m.seeker.reporter_name}</p>
                        <p className="flex items-center gap-1"><Phone className="w-3 h-3" /> {m.seeker.phone1}{m.seeker.phone2 ? ` / ${m.seeker.phone2}` : ""}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      ) : section === "seekers" ? (
        seekers.length === 0 ? (
          <p className="text-center text-neutral-500 py-10 border border-dashed border-neutral-300 rounded-sm" data-testid="no-seekers">
            {t("admin.noSeekers")}
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            {seekers.map((s) => (
              <div key={s.id} className="bg-white border border-neutral-200 rounded-sm p-3 flex flex-col gap-2" data-testid={`seeker-row-${s.id}`}>
                <div className="flex gap-3">
                  <img src={photoUrl(s.photos[0].path)} alt="" className="w-16 h-20 object-cover rounded-sm bg-neutral-200 shrink-0" />
                  <div className="flex-1 min-w-0 text-xs text-neutral-600">
                    <p className="font-semibold text-neutral-900 text-sm">{s.child_name} · {ageRangeLabelT(s.age_range, t)}</p>
                    <p className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {s.municipality}</p>
                    <p className="flex items-center gap-1"><User className="w-3 h-3" /> {s.reporter_name}</p>
                    <p className="flex items-center gap-1"><Phone className="w-3 h-3" /> {s.phone1}{s.phone2 ? ` / ${s.phone2}` : ""}</p>
                    {s.additional_info && <p>{t("admin.info")} {s.additional_info}</p>}
                    {s.lat && s.lng && (
                      <a href={`https://www.google.com/maps?q=${s.lat},${s.lng}`} target="_blank" rel="noopener noreferrer"
                        className="text-[#0066CC] flex items-center gap-1 hover:underline" data-testid={`seeker-pin-${s.id}`}>
                        <Link2 className="w-3 h-3" /> {t("admin.viewPin")} ({s.lat.toFixed(4)}, {s.lng.toFixed(4)})
                      </a>
                    )}
                  </div>
                  <span className={`text-xs font-semibold h-fit px-2 py-1 rounded-sm ${s.status === "localizada" ? "bg-green-50 text-[#166534]" : "bg-amber-50 text-[#B45309]"}`}>{statusLabelT(s.status, t)}</span>
                </div>
                {s.status !== "localizada" && (
                  <button onClick={() => markSeekerLocated(s.id)} data-testid={`mark-seeker-located-${s.id}`}
                    className="w-full bg-[#0066CC] text-white hover:bg-[#0052A3] rounded-sm min-h-[40px] text-sm font-semibold flex items-center justify-center gap-1">
                    <CheckCircle2 className="w-4 h-4" /> {t("admin.markReunited")}
                  </button>
                )}
                <button onClick={() => toggleSeekerVisibility(s.id, !s.hidden)} data-testid={`toggle-seeker-visibility-${s.id}`}
                  className={`w-full rounded-sm min-h-[40px] text-sm font-semibold flex items-center justify-center gap-1 border ${s.hidden ? "bg-white border-[#166534] text-[#166534]" : "bg-white border-[#B91C1C] text-[#B91C1C] hover:bg-red-50"}`}>
                  {s.hidden ? <><Eye className="w-4 h-4" /> {t("admin.showPublic")}</> : <><EyeOff className="w-4 h-4" /> {t("admin.hidePublic")}</>}
                </button>
              </div>
            ))}
          </div>
        )
      ) : (
        abuse.length === 0 ? (
          <p className="text-center text-neutral-500 py-10 border border-dashed border-neutral-300 rounded-sm" data-testid="no-abuse">
            {t("admin.noAbuse")}
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            {abuse.map((a) => (
              <div key={a.id} className="bg-white border border-neutral-200 rounded-sm p-3 flex flex-col gap-2" data-testid={`abuse-${a.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <Flag className="w-4 h-4 text-[#B91C1C]" />
                    <span className="text-sm font-semibold text-neutral-900">
                      {t("admin.alertAbout", { target: a.target_type === "case" ? t("admin.targetCase") : a.target_type === "seeker" ? t("admin.targetSeeker") : t("admin.targetPost") })}
                    </span>
                  </div>
                  <span className={`text-xs font-semibold h-fit px-2 py-1 rounded-sm ${a.status === "revisada" ? "bg-green-50 text-[#166534]" : "bg-red-50 text-[#B91C1C]"}`}>{statusLabelT(a.status, t)}</span>
                </div>
                {a.reason && <p className="text-sm text-neutral-700">{t("admin.reason", { reason: a.reason })}</p>}
                {a.target && (
                  <div className="flex gap-3 border-t border-neutral-100 pt-2">
                    {a.target_type !== "post" && (
                      <img src={photoUrl(a.target_type === "case" ? a.target.photo_path : a.target.photos?.[0]?.path)} alt=""
                        className="w-14 h-18 object-cover rounded-sm bg-neutral-200 shrink-0" />
                    )}
                    <div className="text-xs text-neutral-600 min-w-0">
                      <p className="font-semibold text-neutral-900">
                        {a.target_type === "case"
                          ? (a.target.name_unknown ? t("admin.noName") : a.target.first_name)
                          : a.target_type === "seeker"
                          ? a.target.child_name
                          : (a.target.name || t("wall.anon"))}
                      </p>
                      {a.target_type === "post" ? (
                        <>
                          <p className="break-words whitespace-pre-wrap">{a.target.message}</p>
                          <p className="flex items-center gap-1"><Phone className="w-3 h-3" /> {a.target.contact_phone}</p>
                        </>
                      ) : (
                        <p className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {a.target.municipality}</p>
                      )}
                      <p className="text-neutral-500">{a.target.hidden ? t("admin.hidden") : t("admin.visible")}</p>
                    </div>
                  </div>
                )}
                <div className="flex gap-2">
                  {a.target && (
                    <button data-testid={`abuse-hide-${a.id}`}
                      onClick={() => (a.target_type === "case" ? toggleCaseVisibility : a.target_type === "seeker" ? toggleSeekerVisibility : togglePostVisibility)(a.target_id, !a.target.hidden)}
                      className={`flex-1 rounded-sm min-h-[40px] text-sm font-semibold flex items-center justify-center gap-1 border ${a.target.hidden ? "bg-white border-[#166534] text-[#166534]" : "bg-white border-[#B91C1C] text-[#B91C1C] hover:bg-red-50"}`}>
                      {a.target.hidden ? <><Eye className="w-4 h-4" /> {t("admin.show")}</> : <><EyeOff className="w-4 h-4" /> {t("admin.hide")}</>}
                    </button>
                  )}
                  {a.status !== "revisada" && (
                    <button onClick={() => resolveAbuse(a.id)} data-testid={`abuse-resolve-${a.id}`}
                      className="flex-1 bg-neutral-900 text-white hover:opacity-90 rounded-sm min-h-[40px] text-sm font-semibold">
                      {t("admin.markReviewed")}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

export default function AdminPage() {
  const [authed, setAuthed] = useState(!!getToken());
  const logout = () => { clearToken(); setAuthed(false); };
  return authed ? <Panel onLogout={logout} /> : <Login onLogin={() => setAuthed(true)} />;
}
